<template>
    <!-- Solid, opaque surfaces — NOT Vuetify's `tonal` variant.
         `variant="tonal"` paints the theme colour at --v-activated-opacity (0.12)
         and then draws the text in that same colour. With the light theme's
         success (#13DEB9) that renders a #e3fbf7 panel carrying #13DEB9 text:
         1.08:1 against the white page and 1.59:1 for its own text, i.e. invisible
         (BS-1206). The old warning path had the same problem from the other side —
         a 50%-alpha red gradient over white is ~#e99797, so its white text sat at
         2.25:1.
         These are the same tokens snackbars and toastify already use
         (scss/components/_notifications.scss), so every status surface in the app
         now reads the same: #237a3b at 5.36:1, #b42318 at 6.57:1, white text. -->
    <v-alert
        variant="flat"
        :class="['mb-4', 'bs-alert', isWarningMessage ? 'bs-alert--warning' : 'bs-alert--success']"
        :closable="closable !== false"
    >
        <template v-slot:prepend v-if="isicon != null ? isicon : true">
            <v-icon class="text-24">{{ isWarningMessage ? 'mdi-alert-circle' : 'mdi-checkbox-marked-circle-outline' }}</v-icon>
        </template>
        <div v-html="message"></div>
    </v-alert>
</template>
<script>
export default {
    props: ['message', 'isicon', 'closable'],
    computed: {
        isWarningMessage() {
            return this.message && (
                this.message.includes('⚠️') ||
                this.message.includes('unsaved') ||
                this.message.includes('Save your drawings')
            );
        }
    },
    data() {
        return {};
    },
    mounted() {}
};
</script>
<style scoped>
/* Light fill, dark text, strong accent.
   NOT Vuetify's `tonal` variant, which is where BS-1206 came from: tonal paints
   the theme colour at 12% AND draws the text in that same colour, so success
   rendered #13DEB9 text on a #e3fbf7 panel - 1.59:1, invisible.

   A light fill cannot be what makes the banner noticeable. At 0.94 alpha this
   one paints #c3ddfe, which is 1.39:1 against a white card - barely a tint. The
   visibility comes from the 6px accent bar (#1d4ed8, 6.70:1 against the page)
   and from text that is genuinely dark. That is the trade a light banner makes:
   the edge and the type do the work the fill cannot.

   White text is not an option on these fills - it measures 1.39:1 and would be
   unreadable. Text here is dark on purpose. */
.bs-alert {
    border-radius: 8px !important;
    font-weight: 600;
    line-height: 1.45;
    /* Frosted, not merely see-through: without the blur the 6% of page showing
       through reads as noise under the text. */
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
}

/* Alpha on the BACKGROUND COLOUR, never `opacity` on the element - `opacity`
   fades the text and icon with it, which is the exact invisibility this
   component was fixed for. */
.bs-alert--success {
    background-color: rgba(191, 219, 254, 0.94) !important;
    border: 1px solid rgba(29, 78, 216, 0.45) !important;
    border-left: 6px solid #1d4ed8 !important;
    box-shadow: 0 6px 18px rgba(29, 78, 216, 0.18);
    color: #0b3b6f !important;   /* 8.07:1 on the painted fill */
}

.bs-alert--warning {
    background-color: rgba(254, 202, 202, 0.94) !important;
    border: 1px solid rgba(180, 35, 24, 0.45) !important;
    border-left: 6px solid #b42318 !important;
    box-shadow: 0 6px 18px rgba(180, 35, 24, 0.18);
    color: #7f1d1d !important;   /* 7.07:1 on the painted fill */
}

/* The icon and close affordance live inside VAlert's own markup, so :deep() is
   needed to reach them. Both inherit a muted opacity by default, which would
   undo the contrast the text colour just bought. */
.bs-alert--success :deep(.v-icon),
.bs-alert--success :deep(.v-alert__close),
.bs-alert--success :deep(.v-alert__close .v-btn) {
    color: #0b3b6f !important;
    opacity: 1 !important;
}

.bs-alert--warning :deep(.v-icon),
.bs-alert--warning :deep(.v-alert__close),
.bs-alert--warning :deep(.v-alert__close .v-btn) {
    color: #7f1d1d !important;
    opacity: 1 !important;
}

/* v-html content must not reintroduce a low-contrast colour on the fill. */
.bs-alert :deep(a) {
    color: inherit;
    text-decoration: underline;
}
</style>
